import { type ElementType } from "react";

import {
  ComboBoxItem,
  SingleSelectComboBox,
} from "#components/SingleSelectComboBox/SingleSelectComboBox";
import type { Provider } from "#components/ProviderIcon/formatProvider";
import { cn } from "#utils";

export interface ProviderItem {
  logo: ElementType;
  key: Provider;
}

export interface ProviderSelectorProps {
  providers: ProviderItem[];
  selected: Provider;
  onChange: (provider: Provider) => void;
  formatProvider: (provider: Provider) => string;
  /** Show provider logos alongside names in the trigger and items. Defaults to false. */
  icons?: boolean;
  /** Additional class name applied to the trigger element. */
  className?: string;
}

export function ProviderSelector({
  providers,
  selected,
  onChange,
  formatProvider,
  icons = false,
  className,
}: ProviderSelectorProps) {
  const disabled = providers.length <= 1;

  return (
    <SingleSelectComboBox
      aria-label="provider-selector"
      isDisabled={disabled}
      onSelectionChange={(key) => {
        if (!key) return;
        const found = providers.find((provider) => provider.key === key);
        if (found) onChange(found.key);
      }}
      placeholder="Select a cloud provider..."
      selectedKey={selected}
      triggerClassName={cn("text-label-extra-small", className)}
      withSearch={false}
    >
      {providers.map((provider) => {
        const label = formatProvider(provider.key);
        return (
          <ComboBoxItem
            className={
              icons ? "flex items-center gap-xs text-label-extra-small" : "text-label-extra-small"
            }
            id={provider.key}
            key={provider.key}
            textValue={label}
          >
            {icons && <provider.logo aria-hidden="true" className="size-4 shrink-0" />}
            {label}
          </ComboBoxItem>
        );
      })}
    </SingleSelectComboBox>
  );
}
